import { Controller } from "@hotwired/stimulus";
import type MapBoard from "./map_board_controller";
import type MapDice from "./map_dice_controller";
import { closeOtherPanels } from "./map_panels";

// What the island opens over the panel's own Escape: its popups, and a menu's trigger while the menu is open.
const NESTED = ".cs-popup, [aria-haspopup][aria-expanded='true']";

// One character's live sheet: its path (which names it), and the island's props for it.
type Sheet = { path: string; props: Record<string, unknown> };

// Connects to data-controller="map-sheet", beside map-board on the board root: the live sheet island in a panel
// (docs/architecture/maps.md, "The sheet panel").
export default class extends Controller {
	static targets = ["panel", "toggle", "body", "picker", "loading"];
	static values = { shared: Object, sheets: Array };
	declare readonly panelTarget: HTMLElement;
	declare readonly toggleTarget: HTMLElement;
	declare readonly hasToggleTarget: boolean;
	declare readonly bodyTarget: HTMLElement;
	declare readonly loadingTarget: HTMLTemplateElement;
	declare readonly hasLoadingTarget: boolean;
	declare readonly pickerTarget: HTMLSelectElement;
	declare readonly hasPickerTarget: boolean;
	declare readonly sharedValue: Record<string, unknown>;
	declare readonly sheetsValue: Sheet[];
	current: string | null = null;

	disconnect() {
		this.close();
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	get roller() {
		return (this.application.getControllerForElementAndIdentifier(this.element, "map-dice") as MapDice | null)?.roller;
	}

	get opened() {
		return !this.panelTarget.hidden;
	}

	has(path: string) {
		return this.sheetsValue.some((sheet) => sheet.path === path);
	}

	toggle() {
		if (this.opened) this.close();
		else this.show();
	}

	show(path?: string) {
		closeOtherPanels(this.application, this.element, "map-sheet");
		this.panelTarget.hidden = false;
		if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-pressed", "true");
		this.mount(path ?? this.current ?? this.sheetsValue[0]?.path);
		this.roller?.joinPending();
		(this.hasPickerTarget ? this.pickerTarget : this.panelTarget).focus({ preventScroll: true });
	}

	// A popup or popover opened inside the panel takes the Escape first; so does an input that drops what was typed.
	escape(event: KeyboardEvent) {
		if (event.defaultPrevented || this.panelTarget.querySelector(NESTED)) return;
		// Stopped here, or the board's own Escape would also deselect behind the panel.
		event.preventDefault();
		event.stopPropagation();
		this.close();
	}

	close() {
		if (!this.opened) return;
		// Read before hiding: a hidden element gives its focus up at once.
		const focused = this.panelTarget.contains(document.activeElement);
		this.panelTarget.hidden = true;
		if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-pressed", "false");
		this.bodyTarget.replaceChildren();
		if (focused) (this.hasToggleTarget ? this.toggleTarget : this.board?.canvasTarget)?.focus({ preventScroll: true });
		this.roller?.leavePending();
	}

	open(path: string) {
		if (!this.has(path)) return false;
		this.show(path);
		return true;
	}

	pick() {
		this.mount(this.pickerTarget.value);
	}

	toast({ detail: { message } }: CustomEvent<{ message: string }>) {
		this.board?.toast(message);
	}

	// The island keeps one record, so another sheet means another mount.
	mount(path: string) {
		const sheet = this.sheetsValue.find((entry) => entry.path === path);
		if (!sheet) return;
		this.current = path;
		if (this.hasPickerTarget) this.pickerTarget.value = path;
		const host = document.createElement("div");
		host.dataset.controller = "live-island";
		host.dataset.liveIslandComponentValue = "character_sheet";
		host.dataset.liveIslandPropsValue = JSON.stringify({ ...this.sharedValue, ...sheet.props, mode: "panel" });
		if (this.hasLoadingTarget) host.append(this.loadingTarget.content.cloneNode(true));
		this.bodyTarget.replaceChildren(host);
	}
}
